// WWI aircraft hangar — a big corrugated barrel-vault shed read as a stepped
// stack of thinning khaki boxes, with a huge front opening between two sliding
// doors, a hazard-yellow stripe across the doors (the logistics code and the
// iconic feature) and a windsock on a pole at the apron corner.
import * as THREE from "three";
import { defineModel, styleV3, type Part } from "../../lib";

const { box, cyl, slab, poles } = styleV3;

const KHAKI = "#7a6f4e";
const KHAKI_DK = "#6a6044";
const FIELD = "#6d6f64";
const GUNMETAL = "#4a4d52";
const CANVAS = "#cbbd93";
const TIMBER = "#8b6438";
const YELLOW = "#e8c832"; // hazard yellow — the logistics/fuel roof code
const RED = "#b03a3a";
const OPENING = "#26282a";

const BASE = 0.12;   // foundation slab top
const DEPTH = 2.3;   // hangar length, ridge along z
const FRONT = DEPTH / 2;

// The stepped barrel vault: equal-height layers whose widths follow a
// half-ellipse (half-width 1.12, rise 1.02), so the stack reads as a curved
// corrugated arch, not a pyramid. Alternating khaki tones read as sheeting;
// the crown strip is the hazard-yellow logistics code seen from above.
const LAYER_H = 0.17;
const LAYERS: [number, number, string][] = [0, 1, 2, 3, 4, 5].map((i) => {
  const f = (i + 0.5) / 6; // layer mid-height as a fraction of the rise
  const w = 2.24 * Math.sqrt(1 - f * f);
  const color = i === 5 ? YELLOW : i % 2 ? KHAKI_DK : KHAKI;
  return [w, LAYER_H, color];
});

// Vault layers 0..count-1 stacked on the foundation.
function vault(count: number): Part[] {
  const out: Part[] = [];
  let y = BASE;
  for (let i = 0; i < count; i++) {
    const [w, h, color] = LAYERS[i]!;
    out.push(box(w, h, DEPTH, color, [0, y + h / 2, 0]));
    y += h;
  }
  return out;
}

// The dark mouth of the hangar between the half-open sliding doors.
function opening(h = 0.78): Part {
  return box(0.8, h, 0.06, OPENING, [0, BASE + h / 2, FRONT + 0.01]);
}

// The two field-grey sliding door panels with the yellow stripe across them.
function doors(): Part[] {
  return [
    box(0.72, 0.8, 0.06, FIELD, [-0.78, BASE + 0.4, FRONT + 0.03]),
    box(0.72, 0.8, 0.06, FIELD, [0.78, BASE + 0.4, FRONT + 0.03]),
    box(2.24, 0.15, 0.05, YELLOW, [0, BASE + 0.56, FRONT + 0.07]),
  ];
}

// The windsock on a pole at the apron corner.
function windsock(): Part[] {
  const px = 1.22, pz = 1.22, top = BASE + 1.52;
  return [
    cyl(0.024, 0.03, 1.46, 5, TIMBER, [px, BASE + 0.73, pz]),
    // The red mouth ring, then a canvas cone trailing in the wind (+x).
    cyl(0.105, 0.105, 0.06, 6, RED, [px + 0.03, top, pz], [0, 0, Math.PI / 2]),
    { geometry: new THREE.ConeGeometry(0.1, 0.44, 6), color: CANVAS,
      position: [px + 0.28, top, pz], rotation: [0, 0, -Math.PI / 2] },
  ];
}

// Fuel drums stacked by the door — apron clutter.
function drums(): Part[] {
  return [
    cyl(0.075, 0.075, 0.19, 6, FIELD, [-1.18, BASE + 0.095, 0.95]),
    cyl(0.075, 0.075, 0.19, 6, GUNMETAL, [-1.02, BASE + 0.095, 1.06]),
  ];
}

function foundation(): Part {
  return slab(2.86, 2.86);
}

export const aircraftHangar = defineModel({
  id: "aircraft-hangar",
  tiles: { w: 3, h: 3 },
  batchId: "wwi",
  name: "Aircraft Hangar",
  tags: ["building", "wwi", "military", "aviation", "hangar", "logistics"],
  summary: "A khaki corrugated barrel-vault hangar with a hazard-yellow door stripe and a windsock.",
  description:
    "A Great-War airfield hangar: a stepped corrugated vault in khaki with the " +
    "hazard-yellow logistics code on its crown and a bold yellow stripe across " +
    "the huge sliding front doors. A windsock marks the apron corner.",
  author: "Fable 5",
  footprint: 1.5,
  defaultVariant: "complete",
  variants: [
    { id: "foundation", label: "Foundation", parts: () => [foundation()] },
    {
      id: "lower", label: "Lower",
      parts: () => [foundation(), ...vault(2), ...poles(2.7, 2.7, 0.9)],
    },
    {
      id: "upper", label: "Upper",
      parts: () => [foundation(), ...vault(4), opening(0.62), ...poles(2.7, 2.7, 1.3)],
    },
    {
      id: "complete", label: "Complete",
      parts: () => [
        foundation(), ...vault(6), opening(), ...doors(), ...windsock(), ...drums(),
      ],
    },
  ],
  animations: [],
});
